import type { PullChange } from '../../../shared/api/types';
import type { TaskEntity, TagEntity, ProjectEntity } from '../../../entities/task/model/task.types';
import type { HabitEntity } from '../../../entities/habit/model/habit.types';
import { asHabitFrequency, habitCompletionId } from '../../../entities/habit/model/habit.types';
import { completionId, taskTagId } from '../../../entities/task/model/task.types';
import { db, type SyncEntityType } from './database';

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asNullableString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function asNumberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? Math.trunc(value) : null;
}

function asBoolean(value: unknown): boolean {
  return value === true || value === 1 || value === '1';
}

/**
 * Победа локальной правки при pull: сервер строго новее — применяем сервер.
 * Локально dirty + source='pull' — НЕ трогаем сущность вовсе (см. applyServerEntity).
 */
export function isLocalDirty(updatedAt: string, localUpdatedAt: string | null | undefined): boolean {
  if (localUpdatedAt === undefined || localUpdatedAt === null || localUpdatedAt.length === 0) {
    return false;
  }
  return updatedAt < localUpdatedAt;
}

/**
 * Pull не должен затирать сущность, по которой ещё есть неотправленная правка (outbox).
 * Если outbox пуст, а dirty «залип» — pull проходит и данные восстанавливает.
 */
async function shouldSkipPull(
  entityType: SyncEntityType,
  entityId: string,
  local: { dirty: boolean } | undefined,
): Promise<boolean> {
  if (local === undefined) {
    return false;
  }
  const pending = await db.outbox
    .where('[entity_type+entity_id]')
    .equals([entityType, entityId])
    .count();
  return pending > 0;
}

/**
 * Применяет pull/push-сущность к локальной таблице.
 *
 * - source='pull': пока есть неотправленная правка (outbox) — сущность не трогаем.
 * - source='push': ответ сервера после НАШЕЙ отправки — применяем
 *   (снимает dirty, выравнивает server_revision), кроме случая когда сервер
 *   строго старее локальной ещё dirty-записи.
 */
export async function applyServerEntity(
  entityType: SyncEntityType,
  entityId: string,
  entityPayload: Record<string, unknown> | null,
  serverRevision: number | null,
  deletedFlag: boolean,
  userId: string,
  source: 'pull' | 'push' = 'pull',
): Promise<void> {
  const payload = asRecord(entityPayload);
  const updatedAt = asString(payload['updated_at']);
  const deletedAt = deletedFlag ? asNullableString(payload['deleted_at']) ?? updatedAt : asNullableString(payload['deleted_at']);
  const serverTime = updatedAt.length > 0 ? updatedAt : new Date().toISOString();
  const fromPull = source === 'pull';

  switch (entityType) {
    case 'task': {
      const local = await db.tasks.get(entityId);
      if (fromPull && (await shouldSkipPull(entityType, entityId, local))) {
        return;
      }
      if (
        !fromPull &&
        local !== undefined &&
        local.dirty &&
        isLocalDirty(serverTime, local.updated_at)
      ) {
        return;
      }

      if (deletedAt !== null) {
        if (local === undefined) {
          const tombstone: TaskEntity = {
            id: entityId,
            user_id: userId,
            title: asString(payload['title']),
            description: asString(payload['description']),
            priority: asPriority(payload['priority']),
            due_date: asNullableString(payload['due_date']),
            due_time: asNullableString(payload['due_time']),
            duration_minutes: asNumberOrNull(payload['duration_minutes']),
            all_day: asBoolean(payload['all_day']),
            is_idea: asBoolean(payload['is_idea']),
            completed: asBoolean(payload['completed']),
            completed_at: asNullableString(payload['completed_at']),
            archived_at: asNullableString(payload['archived_at']),
            recurrence_rule: asNullableString(payload['recurrence_rule']),
            project_id: asNullableString(payload['project_id']),
            created_at: asString(payload['created_at'], serverTime),
            updated_at: serverTime,
            deleted_at: deletedAt,
            server_revision: serverRevision,
            dirty: false,
          };
          await db.tasks.put(tombstone);
          return;
        }
        await db.tasks.update(entityId, {
          updated_at: serverTime,
          deleted_at: deletedAt,
          server_revision: serverRevision,
          dirty: false,
        });
        return;
      }

      const next: TaskEntity = {
        id: entityId,
        user_id: userId,
        title: asString(payload['title']),
        description: asString(payload['description']),
        priority: asPriority(payload['priority']),
        due_date: asNullableString(payload['due_date']),
        due_time: asNullableString(payload['due_time']),
        duration_minutes: asNumberOrNull(payload['duration_minutes']),
        all_day: asBoolean(payload['all_day']),
        is_idea: asBoolean(payload['is_idea']),
        completed: asBoolean(payload['completed']),
        completed_at: asNullableString(payload['completed_at']),
        archived_at: asNullableString(payload['archived_at']),
        recurrence_rule: asNullableString(payload['recurrence_rule']),
        project_id: asNullableString(payload['project_id']),
        created_at: asString(payload['created_at'], serverTime),
        updated_at: serverTime,
        deleted_at: null,
        server_revision: serverRevision,
        dirty: false,
      };
      await db.tasks.put(next);
      return;
    }

    case 'tag': {
      const local = await db.tags.get(entityId);
      if (fromPull && (await shouldSkipPull('tag', entityId, local))) {
        return;
      }
      if (
        !fromPull &&
        local !== undefined &&
        local.dirty &&
        isLocalDirty(serverTime, local.updated_at)
      ) {
        return;
      }

      if (deletedAt !== null) {
        if (local === undefined) {
          const tombstone: TagEntity = {
            id: entityId,
            user_id: userId,
            name: asString(payload['name']),
            color: asString(payload['color'], '#38bdf8'),
            created_at: asString(payload['created_at'], serverTime),
            updated_at: serverTime,
            deleted_at: deletedAt,
            server_revision: serverRevision,
            dirty: false,
          };
          await db.tags.put(tombstone);
          return;
        }
        await db.tags.update(entityId, {
          updated_at: serverTime,
          deleted_at: deletedAt,
          server_revision: serverRevision,
          dirty: false,
        });
        return;
      }

      await db.tags.put({
        id: entityId,
        user_id: userId,
        name: asString(payload['name']),
        color: asString(payload['color'], '#38bdf8'),
        created_at: asString(payload['created_at'], serverTime),
        updated_at: serverTime,
        deleted_at: null,
        server_revision: serverRevision,
        dirty: false,
      });
      return;
    }

    case 'project': {
      const local = await db.projects.get(entityId);
      if (fromPull && (await shouldSkipPull('project', entityId, local))) {
        return;
      }
      if (
        !fromPull &&
        local !== undefined &&
        local.dirty &&
        isLocalDirty(serverTime, local.updated_at)
      ) {
        return;
      }

      if (deletedAt !== null) {
        if (local === undefined) {
          const tombstone: ProjectEntity = {
            id: entityId,
            user_id: userId,
            name: asString(payload['name']),
            color: asString(payload['color'], '#38bdf8'),
            created_at: asString(payload['created_at'], serverTime),
            updated_at: serverTime,
            deleted_at: deletedAt,
            server_revision: serverRevision,
            dirty: false,
          };
          await db.projects.put(tombstone);
          return;
        }
        await db.projects.update(entityId, {
          updated_at: serverTime,
          deleted_at: deletedAt,
          server_revision: serverRevision,
          dirty: false,
        });
        return;
      }

      await db.projects.put({
        id: entityId,
        user_id: userId,
        name: asString(payload['name']),
        color: asString(payload['color'], '#38bdf8'),
        created_at: asString(payload['created_at'], serverTime),
        updated_at: serverTime,
        deleted_at: null,
        server_revision: serverRevision,
        dirty: false,
      });
      return;
    }

    case 'checklist_item': {
      const local = await db.checklist_items.get(entityId);
      if (fromPull && (await shouldSkipPull('checklist_item', entityId, local))) {
        return;
      }
      if (
        !fromPull &&
        local !== undefined &&
        local.dirty &&
        isLocalDirty(serverTime, local.updated_at)
      ) {
        return;
      }

      if (deletedAt !== null) {
        if (local === undefined) {
          await db.checklist_items.put({
            id: entityId,
            task_id: asString(payload['task_id']),
            user_id: userId,
            text: asString(payload['text']),
            done: asBoolean(payload['done']),
            sort_order: asNumberOrNull(payload['sort_order']) ?? 0,
            updated_at: serverTime,
            deleted_at: deletedAt,
            server_revision: serverRevision,
            dirty: false,
          });
          return;
        }
        await db.checklist_items.update(entityId, {
          updated_at: serverTime,
          deleted_at: deletedAt,
          server_revision: serverRevision,
          dirty: false,
        });
        return;
      }

      await db.checklist_items.put({
        id: entityId,
        task_id: asString(payload['task_id']),
        user_id: userId,
        text: asString(payload['text']),
        done: asBoolean(payload['done']),
        sort_order: asNumberOrNull(payload['sort_order']) ?? 0,
        updated_at: serverTime,
        deleted_at: null,
        server_revision: serverRevision,
        dirty: false,
      });
      return;
    }

    case 'task_tag': {
      const id = entityId.includes(':') ? entityId : taskTagId(asString(payload['task_id']), asString(payload['tag_id']));
      const local = await db.task_tags.get(id);
      if (fromPull && (await shouldSkipPull('task_tag', id, local))) {
        return;
      }
      if (
        !fromPull &&
        local !== undefined &&
        local.dirty &&
        isLocalDirty(serverTime, local.updated_at)
      ) {
        return;
      }

      if (deletedAt !== null) {
        if (local === undefined) {
          await db.task_tags.put({
            id,
            task_id: asString(payload['task_id']) || id.split(':')[0] || '',
            tag_id: asString(payload['tag_id']) || id.split(':')[1] || '',
            user_id: userId,
            updated_at: serverTime,
            deleted_at: deletedAt,
            server_revision: serverRevision,
            dirty: false,
          });
          return;
        }
        await db.task_tags.update(id, {
          updated_at: serverTime,
          deleted_at: deletedAt,
          server_revision: serverRevision,
          dirty: false,
        });
        return;
      }

      const parts = id.split(':');
      await db.task_tags.put({
        id,
        task_id: asString(payload['task_id']) || parts[0] || '',
        tag_id: asString(payload['tag_id']) || parts[1] || '',
        user_id: userId,
        updated_at: serverTime,
        deleted_at: null,
        server_revision: serverRevision,
        dirty: false,
      });
      return;
    }

    case 'completion': {
      const occurrenceDate = asString(payload['occurrence_date']);
      const id = entityId.includes(':') ? entityId : completionId(asString(payload['task_id']), occurrenceDate);
      const local = await db.completions.get(id);
      if (fromPull && (await shouldSkipPull('completion', id, local))) {
        return;
      }
      if (
        !fromPull &&
        local !== undefined &&
        local.dirty &&
        isLocalDirty(serverTime, local.updated_at)
      ) {
        return;
      }

      const parts = id.split(':');
      const taskId = asString(payload['task_id']) || parts[0] || '';
      const date = occurrenceDate || parts.slice(1).join(':') || '';

      if (deletedAt !== null) {
        if (local === undefined) {
          await db.completions.put({
            id,
            task_id: taskId,
            occurrence_date: date,
            user_id: userId,
            created_at: asString(payload['created_at'], serverTime),
            updated_at: serverTime,
            deleted_at: deletedAt,
            server_revision: serverRevision,
            dirty: false,
          });
          return;
        }
        await db.completions.update(id, {
          updated_at: serverTime,
          deleted_at: deletedAt,
          server_revision: serverRevision,
          dirty: false,
        });
        return;
      }

      await db.completions.put({
        id,
        task_id: taskId,
        occurrence_date: date,
        user_id: userId,
        created_at: asString(payload['created_at'], serverTime),
        updated_at: serverTime,
        deleted_at: null,
        server_revision: serverRevision,
        dirty: false,
      });
      return;
    }

    case 'habit': {
      const local = await db.habits.get(entityId);
      if (fromPull && (await shouldSkipPull('habit', entityId, local))) {
        return;
      }
      if (
        !fromPull &&
        local !== undefined &&
        local.dirty &&
        isLocalDirty(serverTime, local.updated_at)
      ) {
        return;
      }

      const frequency = asHabitFrequency(payload['frequency']);

      if (deletedAt !== null) {
        if (local === undefined) {
          const tombstone: HabitEntity = {
            id: entityId,
            user_id: userId,
            name: asString(payload['name']),
            description: asString(payload['description']),
            color: asString(payload['color'], '#38bdf8'),
            frequency,
            start_date: asString(payload['start_date'], serverTime.slice(0, 10)),
            paused_at: asNullableString(payload['paused_at']),
            created_at: asString(payload['created_at'], serverTime),
            updated_at: serverTime,
            deleted_at: deletedAt,
            server_revision: serverRevision,
            dirty: false,
          };
          await db.habits.put(tombstone);
          return;
        }
        await db.habits.update(entityId, {
          updated_at: serverTime,
          deleted_at: deletedAt,
          server_revision: serverRevision,
          dirty: false,
        });
        return;
      }

      const next: HabitEntity = {
        id: entityId,
        user_id: userId,
        name: asString(payload['name']),
        description: asString(payload['description']),
        color: asString(payload['color'], '#38bdf8'),
        frequency,
        start_date: asString(payload['start_date'], serverTime.slice(0, 10)),
        paused_at: asNullableString(payload['paused_at']),
        created_at: asString(payload['created_at'], serverTime),
        updated_at: serverTime,
        deleted_at: null,
        server_revision: serverRevision,
        dirty: false,
      };
      await db.habits.put(next);
      return;
    }

    case 'habit_completion': {
      const completionDate = asString(payload['completion_date']);
      const id = entityId.includes(':')
        ? entityId
        : habitCompletionId(asString(payload['habit_id']), completionDate);
      const local = await db.habit_completions.get(id);
      if (fromPull && (await shouldSkipPull('habit_completion', id, local))) {
        return;
      }
      if (
        !fromPull &&
        local !== undefined &&
        local.dirty &&
        isLocalDirty(serverTime, local.updated_at)
      ) {
        return;
      }

      const parts = id.split(':');
      const habitId = asString(payload['habit_id']) || parts[0] || '';
      const date = completionDate || parts.slice(1).join(':') || '';

      if (deletedAt !== null) {
        if (local === undefined) {
          await db.habit_completions.put({
            id,
            habit_id: habitId,
            completion_date: date,
            user_id: userId,
            created_at: asString(payload['created_at'], serverTime),
            updated_at: serverTime,
            deleted_at: deletedAt,
            server_revision: serverRevision,
            dirty: false,
          });
          return;
        }
        await db.habit_completions.update(id, {
          updated_at: serverTime,
          deleted_at: deletedAt,
          server_revision: serverRevision,
          dirty: false,
        });
        return;
      }

      await db.habit_completions.put({
        id,
        habit_id: habitId,
        completion_date: date,
        user_id: userId,
        created_at: asString(payload['created_at'], serverTime),
        updated_at: serverTime,
        deleted_at: null,
        server_revision: serverRevision,
        dirty: false,
      });
      return;
    }

    default: {
      const exhaustive: never = entityType;
      throw new Error(`Неизвестный тип сущности: ${String(exhaustive)}`);
    }
  }
}

function asPriority(value: unknown): TaskEntity['priority'] {
  return value === 'urgent' || value === 'high' || value === 'low' ? value : 'medium';
}

export async function applyPullChange(change: PullChange, userId: string): Promise<void> {
  await applyServerEntity(
    change.type,
    change.entity_id,
    change.entity,
    change.revision,
    change.deleted,
    userId,
    'pull',
  );
}

/** Полная очистка локальных сущностей (смена эпохи). Outbox и meta-user не трогаем. */
export async function clearLocalEntities(): Promise<void> {
  await db.tasks.clear();
  await db.tags.clear();
  await db.projects.clear();
  await db.checklist_items.clear();
  await db.task_tags.clear();
  await db.completions.clear();
  await db.habits.clear();
  await db.habit_completions.clear();
}

/** Очистка всех локальных данных пользователя (logout / смена аккаунта). */
export async function clearAllLocalUserData(): Promise<void> {
  await db.tasks.clear();
  await db.tags.clear();
  await db.projects.clear();
  await db.checklist_items.clear();
  await db.task_tags.clear();
  await db.completions.clear();
  await db.habits.clear();
  await db.habit_completions.clear();
  await db.outbox.clear();
}
